import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { confirmDialog } from 'primereact/confirmdialog';
import type { ProjectAllocationItem } from '@brand24-original/llm-types';
import './ProjectsSection.scss';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { ProgressBar } from 'primereact/progressbar';
import { formatLongDate } from '../../../../shared/helpers/date/formatLongDate';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import { DataTable } from '../../../../shared/components/common/data-table/DataTable';
import MoreBtn from '../../../../shared/components/common/more-btn/MoreBtn';
import MetricSwitcher from '../../../../shared/components/metric-switcher/MetricSwitcher';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { TrendTag } from '../../../projects/preview/components/trend-tag/TrendTag';
import useIsMobile from '../../../../shared/hooks/global/UseIsMobile';
import useCanAddProject from '../../../../shared/hooks/account/UseAddProject';
import useToast from '../../../../shared/hooks/global/UseToast';
import useChangeName from '../../../../shared/hooks/projects/UseChangeName';
import { brightDataCountries } from '../../../../shared/utils/utils';
import { MOBILE_PAGINATOR_TEMPLATE } from '../../../../shared/utils/mobile';
import { presetLabel } from '../../../../shared/models/projects/MonitoringInterval';
import {
  PROJECTS,
  PROJECTS_ADD,
  WIZARD_RETURN_AGENCY_HUB,
  WIZARD_RETURN_PARAM,
} from '../../../../shared/routes/path';
import AllocatePromptsDialog, {
  AllocationDrafts,
} from '../allocate-prompts-dialog/AllocatePromptsDialog';
import ShareProjectDialog from '../share-project-dialog/ShareProjectDialog';
import { AGENCY_HUB_PROJECTS_QUERY, AGENCY_HUB_TEAM_QUERY } from '../../agencyHubQueryKeys';
import {
  getProjectsPage,
  removeProject,
  removeProjectsBulk,
  updateAllocations,
} from '../../../../shared/services/projects/ProjectService';
import { useProjectsListKpis } from '../../../../shared/api/hooks/project/stats/useProjectsListKpis';
import { calculateDefaultDateRange } from '../../../../shared/helpers/filters/DateRangeHelper';
import { useAccount } from '../../../../shared/contexts/account/AccountContexts';
import type { ProjectStatus } from '../../../../shared/types/projects/ProjectStatus';
import { AgencyHubProject, ownProjects, toAgencyHubProject } from '../../agencyHubRows';

// No "all" option (user decision 2026-10-21): the list is either the running or the paused projects.
const STATUS_OPTIONS = [
  { key: 'ACTIVE', labelKey: 'active' },
  { key: 'PAUSED', labelKey: 'paused' },
] as const;

/** `/projects/page` sort keys the table offers. */
type SortField = 'projectLabel' | 'created';

const DESKTOP_PAGINATOR_TEMPLATE =
  'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown';

interface ProjectsSectionProps {
  /** The account's prompt pool (`maxMonitorCount`). */
  pool: number;
}

/**
 * The hub's projects list on the "Watched pages" pattern (Sources): frame header with title, status
 * filter, search, "Allocate prompts", "Add new project" and the selection toggle; one flat table (no
 * groups — decision 2026-10-19) served by `/projects/page` the way `/projects` is: search, sort, status
 * and paging on the server, the visibility KPIs by a second call for the page's ids.
 *
 * Writes (2026-10-23): pause / start (row, menu, bulk) and the allocation dialog go through ONE
 * mutation over `PUT /projects/allocations` (the changes only, one transaction on the server); a
 * success refreshes the page, the account (the tiles) and the context list (the dialog's rows). "Edit"
 * opens the project edit dialog (`useChangeName`, with the allocation field). "Share project" is live
 * (`GET /members`, `PUT /share`). Remove (row menu, bulk bar) uses `/projects`' endpoints after a
 * confirmation. The allocation dialog lists EVERY project (the account list from the context), not the
 * page.
 */
const ProjectsSection: React.FC<ProjectsSectionProps> = ({ pool }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { canAddProject } = useCanAddProject();
  const { accountResponse, projectResponse, refetchAccountSilently, refetchProjectsSilently } =
    useAccount();
  const queryClient = useQueryClient();
  const { showToast, toastElement } = useToast();

  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus>('ACTIVE');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<SortField>('created');
  const [sortOrder, setSortOrder] = useState<1 | -1>(-1);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [allocateFor, setAllocateFor] = useState<{ open: boolean; projectId: number | null }>({
    open: false,
    projectId: null,
  });
  const [shareFor, setShareFor] = useState<{ id: number; name: string } | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedSearch(searchText.trim());
      setPage(0);
    }, 300);
    return () => window.clearTimeout(id);
  }, [searchText]);

  const { data } = useQuery({
    queryKey: [
      AGENCY_HUB_PROJECTS_QUERY,
      page,
      pageSize,
      debouncedSearch,
      sortField,
      sortOrder,
      statusFilter,
    ],
    queryFn: () =>
      getProjectsPage({
        page,
        pageSize,
        search: debouncedSearch || undefined,
        sort: sortField,
        order: sortOrder === 1 ? 'ASC' : 'DESC',
        status: statusFilter,
        accountOnly: true,
      }),
    placeholderData: (prev) => prev,
    // Always refetch on mount (the wizard and the project edit come back here); never on focus — the
    // hub's writes refresh the page themselves.
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;

  // Same window as `/projects`: the last 30 days; the endpoint derives the trends itself.
  const kpiRange = useMemo(() => calculateDefaultDateRange(new Date(0)).range, []);
  const pageIds = useMemo(() => items.map((project) => project.id), [items]);
  const { kpis } = useProjectsListKpis(pageIds, kpiRange[0], kpiRange[1], true);

  const rows = useMemo<AgencyHubProject[]>(
    () => items.map((project) => toAgencyHubProject(project, kpis[project.id])),
    [items, kpis]
  );

  // Every project of the account for the allocation dialog (one validated save across all of them).
  const allProjects = useMemo<AgencyHubProject[]>(
    () =>
      ownProjects(accountResponse, projectResponse ?? []).map((project) => toAgencyHubProject(project)),
    [accountResponse, projectResponse]
  );

  // A new page, filter or search hides rows: drop them from the selection, or the bulk bar stays up
  // for rows nobody can see and "select all" would silently replace a wider selection.
  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => rows.some((project) => project.id === id)));
  }, [rows]);

  // Everything the hub writes about a project's allocation state goes through this one mutation: a
  // pause, a start, the dialog's batch — the server applies the list as one transaction. A success
  // refreshes what shows the result: the page (rows), the account (tiles) and the context list (the
  // dialog). A 409 at a start is the pool's answer, shown as the hub's popup; anything else is a toast.
  const refreshAfterWrite = () => {
    void queryClient.invalidateQueries({ queryKey: [AGENCY_HUB_PROJECTS_QUERY] });
    void refetchAccountSilently();
    void refetchProjectsSilently();
  };

  // Removal (row menu, bulk bar) on the endpoints that `/projects` uses; their services swallow errors
  // and resolve to the error object, so success is exactly `true`. A removed project also leaves the
  // guests' chips, hence the team list refreshes too. A page emptied by a bulk removal steps back.
  const { mutate: remove, isPending: removing } = useMutation({
    mutationFn: async (ids: number[]) =>
      (ids.length === 1 ? await removeProject(ids[0]) : await removeProjectsBulk(ids)) === true,
    onSuccess: (removed, ids) => {
      if (!removed) {
        showToast(t('somethingWentWrong'), 'error');
        return;
      }
      refreshAfterWrite();
      void queryClient.invalidateQueries({ queryKey: [AGENCY_HUB_TEAM_QUERY] });
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
      if (page > 0 && rows.every((row) => ids.includes(row.id))) setPage(page - 1);
      showToast(ids.length === 1 ? t('projectRemoved') : t('projectsRemoved', { count: ids.length }));
    },
    onError: () => showToast(t('somethingWentWrong'), 'error'),
  });
  const confirmRemove = (ids: number[]) => {
    if (ids.length === 0) return;
    const single = ids.length === 1 ? rows.find((row) => row.id === ids[0]) : undefined;
    confirmDialog({
      message: single
        ? t('removeProjectConfirm', { name: single.name })
        : t('removeProjectsConfirm', { count: ids.length }),
      header: t('areYouSure'),
      acceptLabel: t('yes'),
      rejectLabel: t('no'),
      defaultFocus: 'reject',
      accept: () => remove(ids),
    });
  };
  const { mutate: applyAllocations, isPending: applying } = useMutation({
    mutationFn: ({
      items: changes,
    }: {
      items: ProjectAllocationItem[];
      kind: 'pause' | 'start' | 'allocations';
    }) => updateAllocations(changes),
    onSuccess: (_summary, { items: changes, kind }) => {
      refreshAfterWrite();
      if (kind === 'allocations') showToast(t('allocationsSaved'));
      else
        showToast(t(kind === 'pause' ? 'projectsPaused' : 'projectsStarted', { count: changes.length }));
    },
    onError: (error: unknown) => {
      refreshAfterWrite();
      const data = (error as { response?: { data?: { error?: string; missing?: number } } }).response
        ?.data;
      if (data?.error === 'POOL_EXCEEDED') {
        confirmDialog({
          message: t('startProjectMissing', { count: data.missing ?? 0 }),
          header: t('startProject'),
          acceptLabel: 'OK',
          rejectClassName: 'hide',
        });
        return;
      }
      showToast(t('somethingWentWrong'), 'error');
    },
  });

  // Row, menu and bulk: only the projects that actually change state travel. A pause asks first — it
  // stops the collection; a start just goes (the server refuses it when the pool is short).
  const onStatusChange = (ids: number[], active: boolean) => {
    const changes: ProjectAllocationItem[] = [...rows, ...allProjects]
      .filter(
        (project, index, list) => list.findIndex((candidate) => candidate.id === project.id) === index
      )
      .filter((project) => ids.includes(project.id) && (project.status === 'ACTIVE') !== active)
      .map((project) => ({ projectId: project.id, active }));
    if (changes.length === 0) return;
    if (active) {
      applyAllocations({ items: changes, kind: 'start' });
      return;
    }
    confirmDialog({
      message: t('confirmPauseProject'),
      header: t('pauseProject'),
      acceptLabel: t('yes'),
      rejectLabel: t('no'),
      defaultFocus: 'reject',
      accept: () => applyAllocations({ items: changes, kind: 'pause' }),
    });
  };

  // The dialog's draft, reduced to the rows that differ from the server: a state change and, for a
  // running project, its new reservation.
  const onAllocationsSave = (drafts: AllocationDrafts) => {
    const changes: ProjectAllocationItem[] = allProjects
      .filter((project) => {
        const draft = drafts[project.id];
        if (!draft) return false;
        const activeNow = project.status === 'ACTIVE';
        return draft.active !== activeNow || (draft.active && draft.allocation !== project.allocation);
      })
      .map((project) => ({
        projectId: project.id,
        active: drafts[project.id].active,
        ...(drafts[project.id].active ? { promptAllocation: drafts[project.id].allocation } : {}),
      }));
    if (changes.length > 0) applyAllocations({ items: changes, kind: 'allocations' });
  };

  // "Edit" in the row menu: the project edit dialog (name, country, schedule, allocation). It patches
  // the context list itself; the hub's page and tiles are refreshed when the save settles.
  const { editName } = useChangeName({ onRefresh: refreshAfterWrite, onSaveEnd: refreshAfterWrite });

  const changeFilter = (status: ProjectStatus) => {
    setStatusFilter(status);
    setPage(0);
  };

  // The wizard's "back" link returns here, not to the projects list.
  const addProjectPath = `${PROJECTS_ADD}?${WIZARD_RETURN_PARAM}=${WIZARD_RETURN_AGENCY_HUB}`;

  const openAllocate = (projectId: number | null) => setAllocateFor({ open: true, projectId });

  const toggleSelection = (id: number, checked: boolean) =>
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((selected) => selected !== id)));

  const nameBody = (project: AgencyHubProject) => (
    <div className="agency-hub-projects__name-cell">
      {/* The status lives in this dot (no separate column — user decision 2026-10-20): the plain 10px circle
          the group header uses next to a group name on /projects (not the pulsing prompt indicator), centred
          against the two-line block, with the status label as its tooltip. */}
      <span
        id={`agency-hub-status-${project.id}`}
        className={`agency-hub-projects__dot agency-hub-projects__dot--${
          project.status === 'ACTIVE' ? 'active' : 'paused'
        }`}
      />
      <CustomTooltip
        tooltipText={project.status === 'ACTIVE' ? 'active' : 'paused'}
        targetRef={`agency-hub-status-${project.id}`}
      />
      <div className="agency-hub-projects__name">
        {/* A new tab (user decision 2026-10-21): the agency keeps the hub open while it looks into a client. */}
        <NavLink
          to={`${PROJECTS}/${project.id}/${project.defaultPeriod.toLowerCase()}/all`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {project.name}
        </NavLink>
        <div className="agency-hub-projects__visibility">
          {project.visibilityPercent === null ? (
            <span className="agency-hub-projects__visibility-value">-</span>
          ) : (
            <>
              <span className="agency-hub-projects__visibility-value">
                {Math.round(project.visibilityPercent)}%
              </span>
            </>
          )}
          {/* Medium trend tag: the value sits next to the arrow; the small one hides it behind a tooltip. */}
          {project.visibilityTrend !== null && project.visibilityTrend !== 0 && (
            <TrendTag
              // Signed like the list's trends ("+1.5%"); the icon follows the sign.
              trendValue={
                project.visibilityTrend > 0 ? `+${project.visibilityTrend}` : project.visibilityTrend
              }
              suffix="%"
              tooltipId={`vis-${project.id}`}
            />
          )}
        </div>
      </div>
    </div>
  );

  const countryBody = (project: AgencyHubProject) => {
    const country = brightDataCountries.find((option) => option.code === project.countryCode);
    if (!country) return null;
    // Same markup as the country dropdown item of the project edit dialog — information only, no editing here.
    return (
      <span className="agency-hub-projects__country">
        <span className={`dropdown-item__icon ico--xs ${country.icon}`} />
        {country.name}
      </span>
    );
  };

  const promptsBody = (project: AgencyHubProject) =>
    project.status === 'PAUSED' ? pausedPromptsBody(project) : activePromptsBody(project);

  // A paused project holds no reservation: show what a start would resume, without the bar.
  const pausedPromptsBody = (project: AgencyHubProject) => (
    <div className="agency-hub-projects__prompts">
      <div className="agency-hub-projects__prompts-line">
        <Button
          id={`agency-hub-limit-${project.id}`}
          className="btn btn--link btn--link-black agency-hub-projects__limit"
          onClick={() => openAllocate(project.id)}
        >
          {project.pausedPrompts > 0
            ? t('agencyHubPausedPrompts', { count: project.pausedPrompts })
            : t('agencyHubPausedNoPrompts')}
          <span className="ico--edit ico--xs" />
        </Button>
        <CustomTooltip
          tooltipText="agencyHubPausedPromptsHint"
          targetRef={`agency-hub-limit-${project.id}`}
        />
      </div>
    </div>
  );

  const activePromptsBody = (project: AgencyHubProject) => (
    <div className="agency-hub-projects__prompts">
      <div className="agency-hub-projects__prompts-line">
        <span className="agency-hub-projects__used">{project.used}</span>
        <Button
          id={`agency-hub-limit-${project.id}`}
          className="btn btn--link btn--link-black agency-hub-projects__limit"
          onClick={() => openAllocate(project.id)}
        >
          {project.allocation === null ? (
            `(${t('agencyHubNoAllocationLimit')})`
          ) : (
            <>
              {t('of')} {project.allocated}
            </>
          )}
          <span className="ico--edit ico--xs" />
        </Button>
        <CustomTooltip tooltipText="changeLimit" targetRef={`agency-hub-limit-${project.id}`} />
      </div>
      {/* No limit, no bar: the project counts only the prompts it uses. */}
      {project.allocation !== null && (
        <ProgressBar
          className="agency-hub-projects__bar progress-bar progress-bar--mini progress-bar--primary"
          value={project.allocated > 0 ? Math.min(100, (project.used / project.allocated) * 100) : 0}
          showValue={false}
        />
      )}
    </div>
  );

  const actionsBody = (project: AgencyHubProject) => (
    <div className="table__cell--actions agency-hub-projects__actions">
      {/* Same control as the bulk pause/start: an icon action-btn with a tooltip. Every paused project
          offers "Start" here; which prompts it would resume is the backend's call (paused_by). */}
      <Button
        className="action-btn"
        id={`agency-hub-toggle-${project.id}`}
        disabled={applying}
        onClick={() => onStatusChange([project.id], project.status !== 'ACTIVE')}
      >
        <span className={`${project.status === 'ACTIVE' ? 'ico--pause' : 'ico--play'} ico--xs`} />
      </Button>
      <CustomTooltip
        tooltipText={project.status === 'ACTIVE' ? 'pauseProject' : 'startProject'}
        targetRef={`agency-hub-toggle-${project.id}`}
      />
      <MoreBtn rowId={project.id} tooltipText="action">
        <div className="buttons-list">
          <Button
            className="action-btn"
            id={`agency-hub-edit-${project.id}`}
            onClick={() => editName(project.id)}
          >
            <span className="ico--edit ico--xs" />
          </Button>
          <CustomTooltip tooltipText="editProject" targetRef={`agency-hub-edit-${project.id}`} />
          <Button
            className="action-btn"
            id={`agency-hub-share-${project.id}`}
            onClick={() => setShareFor({ id: project.id, name: project.name })}
          >
            <span className="ico--users ico--xs" />
          </Button>
          <CustomTooltip tooltipText="shareProject" targetRef={`agency-hub-share-${project.id}`} />
          <Button
            className="action-btn"
            id={`agency-hub-pause-${project.id}`}
            disabled={applying}
            onClick={() => onStatusChange([project.id], project.status !== 'ACTIVE')}
          >
            <span className={`${project.status === 'ACTIVE' ? 'ico--pause' : 'ico--play'} ico--xs`} />
          </Button>
          <CustomTooltip
            tooltipText={project.status === 'ACTIVE' ? 'pauseProject' : 'startProject'}
            targetRef={`agency-hub-pause-${project.id}`}
          />
          <Button
            className="action-btn"
            id={`agency-hub-remove-${project.id}`}
            disabled={removing}
            onClick={() => confirmRemove([project.id])}
          >
            <span className="ico--trash ico--xs" />
          </Button>
          <CustomTooltip tooltipText="removeProject" targetRef={`agency-hub-remove-${project.id}`} />
        </div>
      </MoreBtn>
    </div>
  );

  return (
    <div className="stats__panel agency-hub-projects">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('projects')}
          <CustomTooltip tooltipText="agencyHubProjectsTooltip" addMargin={false} />
          {/* Phone: adding a project as a small button next to the title (the toolbar one is desktop-only). */}
          {isMobile && (
            <Button
              className="btn btn--sm"
              onClick={() => navigate(addProjectPath)}
              disabled={canAddProject === false}
              aria-label={t('addNewProject')}
            >
              <span className="ico--add ico--xs" />
            </Button>
          )}
        </div>
        <div className="page-box__buttons agency-hub-page__toolbar">
          {selectionMode && selectedIds.length > 0 && (
            <div className="page-box__buttons-panel">
              <Button
                className="action-btn"
                id={'agency-hub-bulk-pause'}
                disabled={applying}
                onClick={() => onStatusChange(selectedIds, false)}
              >
                <span className="ico--pause ico--xs" />
              </Button>
              <CustomTooltip tooltipText="bulkPauseProjects" targetRef="agency-hub-bulk-pause" />
              <Button
                className="action-btn"
                id={'agency-hub-bulk-start'}
                disabled={applying}
                onClick={() => onStatusChange(selectedIds, true)}
              >
                <span className="ico--play ico--xs" />
              </Button>
              <CustomTooltip tooltipText="bulkStartProjects" targetRef="agency-hub-bulk-start" />
              <Button
                className="action-btn action-btn--danger"
                id="agency-hub-bulk-remove"
                disabled={removing}
                onClick={() => confirmRemove(selectedIds)}
              >
                <span className="ico--trash ico--xs" />
              </Button>
              <CustomTooltip tooltipText="bulkRemoveProjects" targetRef="agency-hub-bulk-remove" />
            </div>
          )}
          <MetricSwitcher options={STATUS_OPTIONS} value={statusFilter} onChange={changeFilter} />
          <div className="input-search-box">
            <InputText
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className="input-search"
            />
          </div>
          <Button
            className="btn btn--sm btn--secondary"
            onClick={() => openAllocate(null)}
            label={t('allocatePrompts')}
            icon={<span className="ico--settings-2 ico--xs" />}
          />
          {!isMobile && (
            <Button
              className="btn btn--sm"
              onClick={() => navigate(addProjectPath)}
              disabled={canAddProject === false}
              label={t('addNewProject')}
              icon={<span className="ico--add ico--xs" />}
            />
          )}
          <Button
            className={`action-btn${selectionMode ? ' action-btn--active' : ''}`}
            id="agency-hub-select-btn"
            onClick={() =>
              setSelectionMode((prev) => {
                if (prev) setSelectedIds([]);
                return !prev;
              })
            }
          >
            <span className="ico--select ico--xs" />
          </Button>
          <CustomTooltip tooltipText="selectModeProjects" targetRef="agency-hub-select-btn" />
        </div>
      </div>

      <DataTable
        value={rows}
        dataKey="id"
        lazy
        stripedRows
        scrollable
        removableSort
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(e) => {
          // `removableSort`: the third click reports no field / order — back to the default (newest first).
          const field = e.sortField as SortField | undefined;
          const order = e.sortOrder === 1 || e.sortOrder === -1 ? e.sortOrder : null;
          setSortField(field && order ? field : 'created');
          setSortOrder(field && order ? order : -1);
          setPage(0);
        }}
        paginator
        first={page * pageSize}
        rows={pageSize}
        totalRecords={total}
        onPage={(e) => {
          setPage(e.page ?? 0);
          setPageSize(e.rows);
        }}
        rowsPerPageOptions={[10, 25, 50]}
        paginatorClassName="pagination"
        paginatorTemplate={isMobile ? MOBILE_PAGINATOR_TEMPLATE : DESKTOP_PAGINATOR_TEMPLATE}
        currentPageReportTemplate={t('rowsNumber') + ':'}
        pageLinkSize={isMobile ? 3 : 5}
        tableStyle={{ tableLayout: 'auto' }}
        tableClassName={`table ${selectionMode ? 'table--select' : ''}`}
        className="table-wrapper table-wrapper--stack"
        responsiveLayout="stack"
        emptyMessage={() => <EmptyPanel />}
      >
        {selectionMode && (
          <Column
            headerClassName="table__header"
            bodyClassName="table__cell"
            style={{ width: '40px' }}
            header={
              <Checkbox
                checked={rows.length > 0 && rows.every((project) => selectedIds.includes(project.id))}
                onChange={(e) => setSelectedIds(e.checked ? rows.map((project) => project.id) : [])}
              />
            }
            body={(project: AgencyHubProject) => (
              <Checkbox
                checked={selectedIds.includes(project.id)}
                onChange={(e) => toggleSelection(project.id, Boolean(e.checked))}
              />
            )}
          />
        )}
        <Column
          field="projectLabel"
          header={t('projectName')}
          sortable
          headerClassName="table__header"
          bodyClassName="table__cell"
          body={nameBody}
        />
        <Column
          field="domain"
          header={t('domain')}
          headerClassName="table__header"
          bodyClassName="table__cell"
        />
        <Column
          header={t('country')}
          headerClassName="table__header"
          bodyClassName="table__cell"
          body={countryBody}
        />
        {/* Explicit widths as on the Watched pages list: the usage column gets the room for its bar, the
            frequency column stops growing past its (nowrap) header — without them both were auto columns
            and the frequency's long header took a share of the free space. */}
        <Column
          header={t('prompts')}
          headerClassName="table__header"
          bodyClassName="table__cell"
          body={promptsBody}
          style={{ width: '220px', minWidth: '220px' }}
        />
        <Column
          header={t('monitoringSchedule')}
          headerClassName="table__header"
          bodyClassName="table__cell"
          body={(project: AgencyHubProject) => presetLabel(project.monitoringInterval)}
          style={{ width: '140px', minWidth: '140px' }}
        />
        <Column
          field="created"
          header={t('created')}
          sortable
          headerClassName="table__header"
          bodyClassName="table__cell"
          body={(project: AgencyHubProject) => formatLongDate(project.created, i18n.language)}
        />
        <Column
          header={t('action')}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell table__cell--center table__cell--stack-actions"
          body={actionsBody}
        />
      </DataTable>

      {toastElement}
      <AllocatePromptsDialog
        visible={allocateFor.open}
        projects={allProjects}
        pool={pool}
        focusProjectId={allocateFor.projectId}
        onHide={() => setAllocateFor({ open: false, projectId: null })}
        onSave={onAllocationsSave}
      />
      <ShareProjectDialog project={shareFor} onHide={() => setShareFor(null)} />
    </div>
  );
};

export default ProjectsSection;
